import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { Loader2 } from 'lucide-react'
import api from '@/admin/lib/api'
import { setAuthToken } from '@/shared/lib/auth-token'

// Allowed return_to prefixes (mirrored on the backend in
// apps.accounts.constants.ALLOWED_RETURN_PREFIXES). Client-side check is
// defence-in-depth - the backend also validates before passing return_to
// to us, but we re-validate here so a tampered URL can't trick us into an
// open redirect. Prefixes are stored WITHOUT a trailing slash and matched
// on a strict path boundary (see safeReturnTo) so "/settings" works and
// "/registrations-evil" does not slip past "/registrations".
const ALLOWED_RETURN_PREFIXES = [
  '/organization-invite',
  '/dashboard',
  '/settings',
  '/registrations',
  '/exceptions',
  '/claims',
  '/transfers',
  '/lab',
  '/inspections',
  '/staff',
  '/manage',
  '/reporting',
]

function safeReturnTo(value: string | null): string | null {
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  if (value.startsWith('/\\')) return null
  if (/[\r\n\t]/.test(value)) return null
  const onBoundary = ALLOWED_RETURN_PREFIXES.some(
    (p) =>
      value === p ||
      value.startsWith(p + '/') ||
      value.startsWith(p + '?') ||
      value.startsWith(p + '#'),
  )
  if (!onBoundary) return null
  return value
}

/**
 * SSO Callback Page
 *
 * Receives a short-lived authorization code from the SAML ACS redirect,
 * exchanges it for an allauth session token via a backend API call, persists
 * it in the shared token store, and redirects either to the original
 * ``return_to`` path (for invitation-routed SSO) or the dashboard (default).
 *
 * URL: /sso/callback?code=...&tenant_slug=...[&return_to=...]
 */
export function SSOCallbackPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const processed = useRef(false)

  useEffect(() => {
    if (processed.current) return
    processed.current = true

    const code = searchParams.get('code')
    const tenantSlug = searchParams.get('tenant_slug')
    const returnTo = safeReturnTo(searchParams.get('return_to'))

    if (!code) {
      toast.error('SSO authentication failed. Missing authorization code.')
      navigate('/login')
      return
    }

    // Clear code/return_to from URL immediately (replace state to avoid back-button replay)
    window.history.replaceState({}, '', '/sso/callback')

    // Exchange the one-time code for an allauth session token
    api
      .post('/auth/sso/exchange/', { code })
      .then((res) => {
        const sessionToken = res.data.session_token
        if (!sessionToken) {
          throw new Error('Missing session token in exchange response')
        }

        // Persist the session token in the shared store (per-origin localStorage)
        setAuthToken(sessionToken)

        // Store tenant slug for subsequent navigation
        if (tenantSlug) {
          sessionStorage.setItem('tenantSlug', tenantSlug)
        }

        // Invalidate session cache so useSession picks up the new auth
        return queryClient.invalidateQueries({ queryKey: ['session'] })
      })
      .then(() => {
        toast.success('Signed in via SSO')
        navigate(returnTo ?? '/dashboard')
      })
      .catch((err) => {
        toastApiError(err, 'SSO sign-in failed. Please try again.')
        navigate(tenantSlug ? `/${tenantSlug}/login` : '/login')
      })
  }, [searchParams, navigate, queryClient])

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-[var(--green)]" />
        <p className="mt-4 text-[13px] text-[var(--ink-2)]">Completing SSO sign-in...</p>
      </div>
    </div>
  )
}
